// TutorialApi.ts
import axios, { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import Cookies from 'universal-cookie';
import General from '../../../../general';
import config from '../../../../config';
import { PartialBrandTheme, PartialQuestThemeData } from './tutorial.types';

export interface UserAuth {
  userId: string;
  token: string;
}

export interface ApiHeaders {
  apiKey: string;
  userId?: string;
  token?: string;
}

export interface TutorialStep {
  type: string;
  title: string;
  url: string | undefined;
  subheading: string;
  id: string;
  status: boolean;
}

interface TutorialAction {
  actionType: string;
  title: string;
  metadata?: {
    link?: string;
  };
  description?: string;
  actionId: string;
  isCompleted: boolean;
}



export interface QuestResponse {
  success: boolean;
  data: {
    sdkConfig?: {
      uiProps?: {
        questThemeData?: PartialQuestThemeData;
        BrandTheme?: {
          properties: PartialBrandTheme;
        };
        showFooter?: boolean;
        Headers?: any[];
      };
    };
    actions: TutorialAction[];
    campaignVariationId: string;
  };
}

interface GetUserAuthParams {
  apiKey: string;
  entityId: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}

interface ExternalLoginResponse {
  
    userId: string;
    token: string;
  
}

interface VerifyActionResponse {
  success: boolean;
  error?: string;
  data?: any;
}

interface ExternalLoginResponse {
  userId: string;
  token: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token : string;
}

class TutorialApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL = apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      'Content-Type': 'application/json',
      ...headers
    };
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: GetUserAuthParams): Promise<UserAuth | null> {
    try {
      
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");


      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken
        };
      }

      if (uniqueUserId) {
        const headers = this.getAxiosHeaders({
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
        });

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );


        if (!response.data?.success) {
          throw new Error('Invalid response from external login');
        }

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken
        };
      }

      // If no uniqueUserId but we have userId and token, use those
      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async fetchCampaignData(
    entityId: string,
    questId: string,
    headers: ApiHeaders,
    variation?: string
  ): Promise<QuestResponse['data']> { // Note the return type change
    try {
      if (!headers.userId || !headers.token) {
        throw new Error('Missing required headers');
      }

      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;
      
      const response = await axios.get<QuestResponse>(request, { 
        headers: axiosHeaders
      });
      
      if (!response.data?.data) {
        throw new Error('No data received from API');
      }

      return response.data.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }


  async verifyAction(
    entityId: string,
    questId: string,
    headers: ApiHeaders,
    actionId: string,
    campaignVariationId: string
  ): Promise<VerifyActionResponse> {
    try {
      if (!headers.userId || !headers.token) {
        throw new Error('Missing required headers');
      }

      const body = {
        actions: [
          {
            answers: [],
            actionId,
          },
        ],
        campaignVariationId,
      };

      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/verify`;

      const response = await axios.post<ApiResponse<VerifyActionResponse>>(
        request,
        body,
        { headers: axiosHeaders }
      );

      if (!response.data?.data) {
        throw new Error('Invalid response from verify action');
      }

      return response.data.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  processActionsData(actions: TutorialAction[]): TutorialStep[] {
    return actions.map((action): TutorialStep => ({
      type: action.actionType,
      title: action.title,
      url: action.metadata?.link,
      subheading: action.description || "this is the description",
      id: action.actionId,
      status: action.isCompleted,
    }));
  }

  clearCookies(): void {
    this.cookies.remove("externalUserId", { path: "/" });
    this.cookies.remove("questUserId", { path: "/" });
    this.cookies.remove("questUserToken", { path: "/" });
  }

  isValidUserAuth(userAuth: unknown): userAuth is UserAuth {
    return (
      !!userAuth &&
      typeof (userAuth as UserAuth).userId === 'string' &&
      typeof (userAuth as UserAuth).token === 'string'
    );
  }
}

export default TutorialApi;